// ══════════════════════════════════════════════════════════════════════════════
// MESSAGERIE UNIFIÉE COCKPIT PRO
// Connecte tous les messages : Clients, Partenaires, Réseau Pro, Shop
// Style Premium avec filtres "Mes amis" et "Mes pros"
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Send, MoreVertical, Phone, Video, Info, Image, Paperclip,
  Check, CheckCheck, Clock, Star, Archive, Trash2, ChevronLeft, ChevronRight,
  Store, User, Package, Briefcase, Megaphone, BadgeCheck, Camera,
  Mic, Smile, Heart, Plus, Settings, Bell, ArrowLeft, Ban, Flag,
  ShieldOff, AlertTriangle, X, Filter, Users, Handshake, FileText,
  Building2, HardHat, Sparkles, MessageCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import ResponseAssistant from '../../components/ai/ResponseAssistant';

// ══════════════════════════════════════════════════════════════════════════════
// DONNÉES DÉMO - CONVERSATIONS COCKPIT PRO (VIDE pour nouvel utilisateur)
// ══════════════════════════════════════════════════════════════════════════════

const DEMO_CONVERSATIONS = [];

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT: Filtres Premium Horizontaux
// ══════════════════════════════════════════════════════════════════════════════

const FILTER_OPTIONS = [
  { id: 'all', label: 'Tous', icon: null, count: null },
  { id: 'unread', label: 'Non lus', icon: Bell, count: 'unread' },
  { id: 'friends', label: 'Mes amis', icon: Heart, count: 'friends' },
  { id: 'pros', label: 'Mes pros', icon: Briefcase, count: 'pros' },
  { id: 'clients', label: 'Clients', icon: Users, count: 'clients' },
  { id: 'partenaires', label: 'Partenaires', icon: Handshake, count: 'partenaires' },
  { id: 'reseau', label: 'Réseau', icon: Building2, count: 'reseau' },
];

function PremiumFilters({ filter, setFilter, conversations }) {
  const scrollRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  
  const handleScrollFilters = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  
  const counts = {
    unread: conversations.filter(c => c.unread > 0).length,
    friends: conversations.filter(c => c.isFriend).length,
    pros: conversations.filter(c => c.isPro).length,
    clients: conversations.filter(c => c.isClient).length,
    partenaires: conversations.filter(c => c.type === 'partenaire' || c.type === 'fournisseur').length,
    reseau: conversations.filter(c => c.category === 'reseau').length,
  };
  
  return (
    <div className="bg-white border-b border-gray-200 p-3">
      {/* Filtres Mobile */}
      <div className="sm:hidden">
        <div className="relative flex items-center">
          <div 
            ref={scrollRef}
            onScroll={handleScrollFilters}
            className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {FILTER_OPTIONS.map((option) => {
              const isActive = filter === option.id;
              const count = option.count ? counts[option.count] : null;
              const Icon = option.icon;
              
              return (
                <button
                  key={option.id}
                  onClick={() => setFilter(option.id)}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                    isActive
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {Icon && <Icon size={12} />}
                  {option.label}
                  {count !== null && count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      isActive ? 'bg-white/20' : 'bg-gray-100'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Flèche orange discrète */}
          {showScrollHint && (
            <div className="flex-shrink-0 flex items-center pointer-events-none">
              <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
            </div>
          )}
        </div>
      </div>

      {/* Filtres Desktop */}
      <div className="hidden sm:flex flex-wrap gap-2">
        {FILTER_OPTIONS.map((option) => {
          const isActive = filter === option.id;
          const count = option.count ? counts[option.count] : null;
          const Icon = option.icon;
          
          return (
            <button
              key={option.id}
              onClick={() => setFilter(option.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                isActive
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {Icon && <Icon size={14} />}
              {option.label}
              {count !== null && count > 0 && (
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  isActive ? 'bg-white/20' : 'bg-gray-200'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT: Liste des conversations
// ══════════════════════════════════════════════════════════════════════════════

function ConversationList({ conversations, selectedId, onSelect, filter, setFilter }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = conversations.filter(c => {
    const matchesSearch = c.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.context?.title?.toLowerCase().includes(searchQuery.toLowerCase()));
    
    if (filter === 'all') return matchesSearch;
    if (filter === 'unread') return matchesSearch && c.unread > 0;
    if (filter === 'friends') return matchesSearch && c.isFriend;
    if (filter === 'pros') return matchesSearch && c.isPro;
    if (filter === 'clients') return matchesSearch && c.isClient;
    if (filter === 'partenaires') return matchesSearch && (c.type === 'partenaire' || c.type === 'fournisseur');
    if (filter === 'reseau') return matchesSearch && c.category === 'reseau';
    return matchesSearch;
  });

  const getContextIcon = (type) => {
    switch (type) {
      case 'devis': return <FileText size={12} className="text-blue-500" />;
      case 'partenaire': return <Handshake size={12} className="text-amber-500" />;
      case 'fournisseur': return <Package size={12} className="text-purple-500" />;
      case 'client': return <Users size={12} className="text-emerald-500" />;
      case 'prospect': return <User size={12} className="text-red-500" />;
      case 'reseau': return <Building2 size={12} className="text-pink-500" />;
      default: return <User size={12} className="text-gray-400" />;
    }
  };

  const getContextColor = (type) => {
    switch (type) {
      case 'devis': return 'bg-blue-50 text-blue-600';
      case 'partenaire': return 'bg-amber-50 text-amber-600';
      case 'fournisseur': return 'bg-purple-50 text-purple-600';
      case 'client': return 'bg-emerald-50 text-emerald-600';
      case 'prospect': return 'bg-red-50 text-red-600';
      case 'reseau': return 'bg-pink-50 text-pink-600';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b border-gray-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold text-gray-900">Messages</h1>
          <div className="flex items-center gap-1">
            <button className="p-2.5 hover:bg-gray-100 rounded-full transition-colors">
              <Filter size={20} className="text-gray-500" />
            </button>
            <button className="p-2.5 hover:bg-gray-100 rounded-full transition-colors">
              <Settings size={20} className="text-gray-500" />
            </button>
          </div>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-gray-100 border-0 rounded-2xl focus:ring-2 focus:ring-[#1E3A5F] text-base"
          />
        </div>
      </div>
      
      {/* Filtres Premium */}
      <PremiumFilters 
        filter={filter} 
        setFilter={setFilter} 
        conversations={conversations}
      />

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-[#1E3A5F]/10 to-[#F97316]/10 rounded-full flex items-center justify-center">
              <MessageCircle size={32} className="text-[#1E3A5F]" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Aucun message pour l'instant</h3>
            <p className="text-sm text-gray-500 mb-4">
              Tes conversations avec tes clients, partenaires et le réseau apparaîtront ici.
            </p>
            <Link
              to="/cockpit-pro/commercial/clients"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] text-white text-sm font-medium rounded-xl hover:bg-[#2d4a6f] transition-colors"
            >
              <Plus size={16} />
              Ajouter un client
            </Link>
          </div>
        ) : (
          filtered.map((conv) => (
            <motion.button
              key={conv.id}
              whileTap={{ scale: 0.98 }}
              onClick={() => onSelect(conv)}
              className={`w-full p-4 flex items-start gap-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                selectedId === conv.id ? 'bg-[#1E3A5F]/5' : ''
              }`}
            >
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg"
                  style={{ backgroundColor: conv.userColor }}
                >
                  {conv.userName.charAt(0)}
                </div>
                {conv.isOnline && (
                  <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-[3px] border-white rounded-full" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-gray-900 truncate">{conv.userName}</h4>
                  {conv.verified && <BadgeCheck size={14} className="text-[#1E3A5F] fill-[#1E3A5F] flex-shrink-0" />}
                  {conv.isFriend && <Heart size={12} className="text-pink-500 fill-pink-500 flex-shrink-0" />}
                  <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{conv.lastMessageTime}</span>
                </div>
                
                {/* Context badge */}
                {conv.context && (
                  <div className="flex items-center gap-1.5 mb-1">
                    {getContextIcon(conv.type)}
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${getContextColor(conv.type)}`}>
                      {conv.context.type}
                    </span>
                    <span className="text-xs text-gray-500 truncate">{conv.context.title}</span>
                  </div>
                )}
                
                {/* Métier Pro */}
                {!conv.context && conv.userMetier && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <HardHat size={12} className="text-gray-400" />
                    <span className="text-xs text-gray-500">{conv.userMetier}</span>
                  </div>
                )}
                
                <p className={`text-sm truncate ${conv.unread ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                  {conv.lastMessage}
                </p>
              </div>

              {/* Unread badge */}
              {conv.unread > 0 && (
                <span className="w-6 h-6 bg-[#1E3A5F] text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">
                  {conv.unread}
                </span>
              )}
            </motion.button>
          ))
        )}
      </div>
      
      {/* Badge DÉMO */}
      <div className="p-3 border-t border-gray-100 bg-gray-50">
        <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
          <span className="bg-[#1E3A5F]/10 px-2 py-0.5 rounded font-semibold text-[#1E3A5F]">COCKPIT PRO</span>
          <span>Messagerie unifiée</span>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT: Zone de chat
// ══════════════════════════════════════════════════════════════════════════════

function ChatArea({ conversation, onSendMessage, onBack }) {
  const [message, setMessage] = useState('');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showResponseAssistant, setShowResponseAssistant] = useState(false);
  const [selectedMessageForHelp, setSelectedMessageForHelp] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages]);

  const handleSend = () => {
    if (!message.trim()) return;
    onSendMessage(message);
    setMessage('');
    inputRef.current?.focus();
  };
  
  const openResponseHelper = (messageText) => {
    setSelectedMessageForHelp(messageText);
    setShowResponseAssistant(true);
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 p-8">
        <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-4">
          <Send size={32} className="text-gray-400" />
        </div>
        <p className="font-semibold text-gray-900">Tes messages</p>
        <p className="text-sm text-gray-500 text-center mt-1">
          Sélectionne une conversation pour voir les échanges
        </p>
      </div>
    );
  }

  const getContextBadge = () => {
    if (!conversation.context) return null;
    
    const colors = {
      devis: 'bg-blue-500',
      partenaire: 'bg-amber-500',
      fournisseur: 'bg-purple-500',
      client: 'bg-emerald-500',
      prospect: 'bg-red-500',
    };
    
    return (
      <div className={`${colors[conversation.type] || 'bg-gray-500'} text-white text-[10px] font-bold px-2 py-1 rounded-full`}>
        {conversation.context.type}
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-white overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-100 flex items-center gap-3 px-4 py-3 z-10">
        <button 
          onClick={onBack}
          className="lg:hidden p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-full -ml-2 transition-colors"
        >
          <ArrowLeft size={22} className="text-gray-700" />
        </button>
        
        <div 
          className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
          style={{ backgroundColor: conversation.userColor }}
        >
          {conversation.userName.charAt(0)}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{conversation.userName}</h3>
            {conversation.verified && <BadgeCheck size={14} className="text-[#1E3A5F] fill-[#1E3A5F] flex-shrink-0" />}
            {getContextBadge()}
          </div>
          <p className="text-xs text-gray-500">
            {conversation.isOnline ? 'En ligne' : 'Hors ligne'}
            {conversation.userMetier && ` • ${conversation.userMetier}`}
          </p>
        </div>
        
        <div className="flex items-center gap-1">
          <button className="p-2.5 hover:bg-gray-100 rounded-full transition-colors">
            <Phone size={18} className="text-gray-500" />
          </button>
          <button className="p-2.5 hover:bg-gray-100 rounded-full transition-colors">
            <Video size={18} className="text-gray-500" />
          </button>
          <div className="relative">
            <button 
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className="p-2.5 hover:bg-gray-100 rounded-full transition-colors"
            >
              <MoreVertical size={18} className="text-gray-500" />
            </button>
            
            <AnimatePresence>
              {showMoreMenu && (
                <>
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-40"
                    onClick={() => setShowMoreMenu(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50"
                  >
                    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left">
                      <FileText size={18} className="text-[#1E3A5F]" />
                      <span className="font-medium text-gray-700">Créer un devis</span>
                    </button>
                    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left border-t border-gray-100">
                      <Star size={18} className="text-amber-500" />
                      <span className="font-medium text-gray-700">Marquer important</span>
                    </button>
                    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left border-t border-gray-100">
                      <Archive size={18} className="text-gray-500" />
                      <span className="font-medium text-gray-700">Archiver</span>
                    </button>
                    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 text-left border-t border-gray-100">
                      <Ban size={18} className="text-red-500" />
                      <span className="font-medium text-red-600">Bloquer</span>
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Context Card */}
      {conversation.context && (
        <div className="flex-shrink-0 px-4 py-3 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-200">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              conversation.type === 'devis' ? 'bg-blue-100' : 
              conversation.type === 'partenaire' ? 'bg-amber-100' : 
              conversation.type === 'fournisseur' ? 'bg-purple-100' : 'bg-emerald-100'
            }`}>
              {conversation.type === 'devis' && <FileText size={20} className="text-blue-600" />}
              {conversation.type === 'partenaire' && <Handshake size={20} className="text-amber-600" />}
              {conversation.type === 'fournisseur' && <Package size={20} className="text-purple-600" />}
              {(conversation.type === 'client' || conversation.type === 'prospect') && <Users size={20} className="text-emerald-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{conversation.context.title}</p>
              <p className="text-xs text-[#1E3A5F] font-semibold">
                {conversation.context.budget || conversation.context.status}
              </p>
            </div>
            <button className="text-xs text-[#1E3A5F] font-medium hover:underline">
              Voir
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {conversation.messages.map((msg, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${msg.isMe ? 'justify-end' : 'justify-start'} group`}
          >
            <div className={`max-w-[75%]`}>
              <div
                className={`px-4 py-2.5 rounded-2xl ${
                  msg.isMe
                    ? 'bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-900 rounded-bl-md'
                }`}
              >
                <p className="text-sm">{msg.text}</p>
              </div>
              <div className={`flex items-center gap-2 mt-1 ${msg.isMe ? 'justify-end' : ''}`}>
                <span className="text-[10px] text-gray-400">{msg.time}</span>
                {msg.isMe && (
                  msg.read 
                    ? <CheckCheck size={12} className="text-[#1E3A5F]" />
                    : <Check size={12} className="text-gray-400" />
                )}
                {/* Bouton Aide à répondre pour les messages reçus */}
                {!msg.isMe && (
                  <button
                    onClick={() => openResponseHelper(msg.text)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-[#1E3A5F] to-[#3B82F6] text-white text-[10px] font-medium rounded-full hover:shadow-md"
                  >
                    <Sparkles size={10} />
                    Aide
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 p-4 border-t border-gray-100 bg-white">
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <Camera size={20} className="text-gray-500" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <Image size={20} className="text-gray-500" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <Paperclip size={20} className="text-gray-500" />
          </button>
          
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Écris un message..."
              className="w-full px-4 py-2.5 bg-gray-100 border-0 rounded-full focus:ring-2 focus:ring-[#1E3A5F] text-sm pr-10"
            />
            <button className="absolute right-3 top-1/2 -translate-y-1/2">
              <Smile size={20} className="text-gray-400" />
            </button>
          </div>
          
          {message.trim() ? (
            <motion.button
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              onClick={handleSend}
              className="p-2.5 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] text-white rounded-full"
            >
              <Send size={18} />
            </motion.button>
          ) : (
            <button className="p-2.5 hover:bg-gray-100 rounded-full">
              <Mic size={20} className="text-gray-500" />
            </button>
          )}
        </div>
      </div>
      
      {/* Modal Response Assistant */}
      {createPortal(
        <AnimatePresence>
          {showResponseAssistant && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/60"
              onClick={() => setShowResponseAssistant(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <ResponseAssistant
                  onClose={() => setShowResponseAssistant(false)}
                  initialMessage={selectedMessageForHelp}
                  initialContext="message"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessagerieProPage() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Récupérer le state passé en navigation (depuis CRM)
  const locationState = location.state || {};
  
  const [conversations, setConversations] = useState(DEMO_CONVERSATIONS);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [hasOpenedFromState, setHasOpenedFromState] = useState(false);

  // Charger les vraies conversations au montage
  useEffect(() => {
    fetchConversations();
  }, []);
  
  // Ouvrir automatiquement une conversation si passée en state
  useEffect(() => {
    if (locationState?.openConversationId && !hasOpenedFromState && !loading) {
      const convToOpen = conversations.find(c => c.id === locationState.openConversationId);
      if (convToOpen) {
        handleSelectConversation(convToOpen);
        setHasOpenedFromState(true);
      } else {
        // La conversation vient d'être créée, on la crée localement pour l'afficher
        const newConv = {
          id: locationState.openConversationId,
          type: 'client',
          category: 'client',
          userName: locationState.clientName || 'Client',
          userColor: '#F97316',
          isOnline: false,
          verified: false,
          isFriend: false,
          isPro: false,
          isClient: true,
          context: {
            type: 'CRM Client',
            title: locationState.clientName || 'Nouveau contact',
            status: 'Actif'
          },
          lastMessage: 'Conversation démarrée',
          lastMessageTime: 'À l\'instant',
          unread: 0,
          messages: []
        };
        setConversations(prev => [newConv, ...prev]);
        setSelectedConversation(newConv);
        setShowChat(true);
        setHasOpenedFromState(true);
      }
    }
  }, [locationState, loading, hasOpenedFromState]);
  
  const fetchConversations = async () => {
    const token = localStorage.getItem('client_token');
    
    try {
      const response = await fetch(`${API_URL}/api/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const realConvs = (data.conversations || []).map(conv => ({
          id: conv.id,
          type: conv.crm_client_id ? 'client' : (conv.type === 'group' ? 'reseau' : 'client'),
          category: conv.crm_client_id ? 'client' : 'reseau',
          userName: conv.client_name || conv.group_name || conv.display_name || 'Contact',
          userColor: '#F97316',
          isOnline: false,
          verified: false,
          isFriend: false,
          isPro: false,
          isClient: !!conv.crm_client_id,
          context: conv.crm_client_id ? {
            type: 'CRM Client',
            title: conv.client_name || 'Client',
            status: 'Actif'
          } : null,
          lastMessage: conv.last_message_text || 'Aucun message',
          lastMessageTime: conv.last_message_at ? new Date(conv.last_message_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '',
          unread: conv.unread_count || 0,
          messages: []
        }));
        
        // Fusionner avec les démos pour avoir du contenu
        setConversations([...realConvs, ...DEMO_CONVERSATIONS]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setShowChat(true);
    setConversations(prev => prev.map(c => 
      c.id === conv.id ? { ...c, unread: 0 } : c
    ));
  };

  const handleSendMessage = (text) => {
    if (!selectedConversation) return;
    
    const newMessage = {
      text,
      isMe: true,
      time: 'À l\'instant',
      read: false,
    };

    setConversations(prev => prev.map(c => 
      c.id === selectedConversation.id 
        ? { 
            ...c, 
            messages: [...c.messages, newMessage],
            lastMessage: text,
            lastMessageTime: 'À l\'instant'
          } 
        : c
    ));

    setSelectedConversation(prev => ({
      ...prev,
      messages: [...prev.messages, newMessage]
    }));
  };

  return (
    <div className="h-[calc(100vh-120px)] flex bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Conversations List */}
      <div className={`w-full lg:w-96 lg:border-r lg:border-gray-200 flex flex-col ${showChat ? 'hidden lg:flex' : ''}`}>
        <ConversationList
          conversations={conversations}
          selectedId={selectedConversation?.id}
          onSelect={handleSelectConversation}
          filter={filter}
          setFilter={setFilter}
        />
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col ${!showChat ? 'hidden lg:flex' : ''}`}>
        <ChatArea
          conversation={selectedConversation}
          onSendMessage={handleSendMessage}
          onBack={() => setShowChat(false)}
        />
      </div>
    </div>
  );
}
